import { lumi } from "@/lib/lumi";
import type { JobInput } from "@/hooks/useJobs";

interface AlertSub {
  _id?: string;
  email?: string;
  mineral?: string;
  type?: string;
  experience?: string;
  active?: boolean;
}

function matchesAlert(sub: AlertSub, job: JobInput): boolean {
  if (sub?.active === false) return false;
  const mineralOk = !sub?.mineral || sub.mineral === "All" || sub.mineral === job.mineral;
  const typeOk = !sub?.type || sub.type === "All" || sub.type === job.type;
  const expOk =
    !sub?.experience || sub.experience === "All" || sub.experience === job.experience;
  return mineralOk && typeOk && expOk;
}

function escapeHtml(value: string): string {
  return String(value || "")
    .replace(/&/g, "&" + "amp;")
    .replace(/</g, "&" + "lt;")
    .replace(/>/g, "&" + "gt;")
    .replace(/"/g, "&" + "quot;");
}

function buildHtml(job: JobInput): string {
  const title = escapeHtml(job.title);
  const company = escapeHtml(job.company);
  const location = escapeHtml(job.location);
  const salary = escapeHtml(job.salary);
  const mineral = escapeHtml(job.mineral);
  const type = escapeHtml(job.type);
  const experience = escapeHtml(job.experience);
  const description = escapeHtml(job.description || "A new mining role has been posted on MineralJobs Board.");

  return `
    <div style="font-family:Arial,sans-serif;background:#111827;color:#e5e7eb;padding:24px">
      <div style="max-width:560px;margin:0 auto;background:#1a1f2e;border:1px solid #374151;border-radius:12px;padding:24px">
        <p style="color:#f97316;font-size:12px;letter-spacing:1px;text-transform:uppercase;margin:0 0 8px">MineralJobs Board</p>
        <h1 style="color:#ffffff;font-size:22px;margin:0 0 12px">New mining job: ${title}</h1>
        <p style="color:#9ca3af;margin:0 0 16px">${company} · ${location}</p>
        <table style="width:100%;border-collapse:collapse;margin-bottom:16px">
          <tr>
            <td style="color:#9ca3af;padding:4px 0">Salary</td>
            <td style="color:#fb923c;text-align:right">${salary}</td>
          </tr>
          <tr>
            <td style="color:#9ca3af;padding:4px 0">Mineral</td>
            <td style="color:#ffffff;text-align:right">${mineral}</td>
          </tr>
          <tr>
            <td style="color:#9ca3af;padding:4px 0">Type</td>
            <td style="color:#ffffff;text-align:right">${type}</td>
          </tr>
          <tr>
            <td style="color:#9ca3af;padding:4px 0">Experience</td>
            <td style="color:#ffffff;text-align:right">${experience}</td>
          </tr>
        </table>
        <p style="color:#d1d5db;line-height:1.5">${description}</p>
        <p style="margin:20px 0 0">
          <a href="https://www.mineraljobs.com/" style="display:inline-block;background:#f97316;color:#ffffff;text-decoration:none;padding:10px 16px;border-radius:8px;font-weight:bold">View jobs on MineralJobs Board</a>
        </p>
      </div>
    </div>
  `;
}

export async function notifyJobAlertSubscribers(job: JobInput): Promise<number> {
  try {
    const result = await lumi.entities.job_alerts.list({
      filter: { active: true },
      limit: 50,
    });
    const list = (result?.list || []) as AlertSub[];
    const matches = list.filter((sub) => matchesAlert(sub, job) && typeof sub?.email === "string");
    const uniqueEmails = Array.from(new Set(matches.map((sub) => sub.email as string)));
    if (uniqueEmails.length === 0) return 0;

    const subject = `New ${job.mineral} job: ${job.title} at ${job.company}`;
    const html = buildHtml(job);
    const batches: string[][] = [];
    for (let i = 0; i < uniqueEmails.length; i += 50) {
      batches.push(uniqueEmails.slice(i, i + 50));
    }

    for (const batch of batches) {
      try {
        await lumi.tools.email.send({
          to: batch.length === 1 ? batch[0] : batch,
          subject,
          fromName: "MineralJobs Board",
          html,
          text: `${job.title} at ${job.company} in ${job.location}. Salary: ${job.salary}. Type: ${job.type}.`,
        });
      } catch (err) {
        console.error("Job alert email batch failed:", err);
      }
    }
    return uniqueEmails.length;
  } catch (err) {
    console.error("Failed to notify job alert subscribers:", err);
    return 0;
  }
}
